import {DateTime} from 'luxon';
import {FC} from 'react';
import {convertToIso} from '~/common/utils';
import {isFabricWebview} from '~/common/utils/webview';
import {Typography} from '~library/atoms/Typography';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {AddToCalendar} from '~library/molecules/AddToCalendar';
import {largishAndUpQuery} from '~libSass/base/_exports.sass';

let dateFirstLine;
let dateSecondLine;

const getFormattedDateStrings = (
  start: string,
  end: string | null,
  timezoneAbbr: string,
  CalendarLineIconRatio: number,
  preview = false
) => {
  const startDateTime = new Date(start);
  const endDateTime = end ? new Date(end) : null;
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    year: 'numeric',
    day: 'numeric',
  };
  const startDate = startDateTime.toLocaleDateString('en-US', dateOptions);
  const endDate = endDateTime ? endDateTime.toLocaleDateString('en-US', dateOptions) : null;
  const startTime = DateTime.fromISO(convertToIso(start)).toFormat('h:mm a');
  const endTime = end ? DateTime.fromISO(convertToIso(end)).toFormat('h:mm a') : null;

  let emptyOrInvalidDate = false;
  if (startDate === 'Invalid Date') {
    dateFirstLine = `Pick a date & time`;
    emptyOrInvalidDate = true;
    if (endDate && endDate !== 'Invalid Date') {
      dateSecondLine = `to ${endDate}, ${endTime} ${timezoneAbbr}`;
    } else {
      dateSecondLine = ``;
    }
  } else if (endDate && startDate !== endDate) {
    dateFirstLine = `${startDate}, ${startTime}`;
    dateSecondLine = `to ${endDate}, ${endTime} ${timezoneAbbr}`;
  } else {
    dateFirstLine = `${startDate}`;
    dateSecondLine = `${startTime} ${endTime ? `- ${endTime} ${timezoneAbbr}` : timezoneAbbr}`;
  }

  return (
    <div className="date-calendar__wrapper">
      <Typography
        variant="paragraph1"
        data-qa-id="event-date-time-string"
        classNames="view-invitation__details-module__section-body"
        data-qa-value={`${dateFirstLine} ${
          dateSecondLine[0] === ' ' ? dateSecondLine.substring(1) : dateSecondLine
        }`}
      >
        <Typography
          variant="paragraph1"
          as="span"
          classNames="view-invitation__details-module__section-body"
          color={emptyOrInvalidDate && preview ? 'pebble' : 'charcoal'}
        >
          {dateFirstLine}
        </Typography>
        {dateSecondLine && (
          <>
            <br />
            {dateSecondLine}
          </>
        )}
      </Typography>
      {/* TODO: Requested in EVT-31608; will follow up shortly after 1458 with more long-term fix to support AddToCalendar in ViewInvitation webview */}
      {isFabricWebview() || preview ? null : <AddToCalendar ratio={CalendarLineIconRatio} />}
    </div>
  );
};

interface FormattedDateStringProps {
  start: string | Date;
  end: string | Date | null;
  timezoneAbbr: string;
  preview?: boolean;
}

export const FormattedDateString: FC<FormattedDateStringProps> = ({
  start,
  end,
  timezoneAbbr,
  preview,
}) => {
  const {matches: largeScreens} = useMatchQuery(`(${largishAndUpQuery})`);
  const startDate = start ? new Date(start).toISOString() : '';
  const endDate = end ? new Date(end).toISOString() : null;

  if (!startDate) {
    return (
      <Typography
        variant="paragraph1"
        data-qa-id="event-date-time-string"
        classNames="view-invitation__details-module__section-body"
        data-qa-value="Pick a date & time"
        color={preview ? 'pebble' : 'charcoal'}
      >
        Pick a date & time
      </Typography>
    );
  }

  return getFormattedDateStrings(
    startDate,
    endDate,
    timezoneAbbr,
    largeScreens ? 0.75 : 0.5,
    preview
  );
};
